import { omit } from 'remeda';
import z from 'zod';

import { ParagraphCodec, PlateParagraph, UnityParagraph } from './Paragraph';
import {
    CURRENT_VERSION,
    ElementId,
    emptyChildren,
    EmptyChildren,
    UnityElementId,
    Version,
} from '../commons';
import { ELEMENT_TYPES, FACEBOOK_MIME_TYPES, MIME_TYPES } from '../constants';

export const UnityFacebook = z.object({
    type: z.literal(ELEMENT_TYPES.FACEBOOK),
    mimetype: z.enum(FACEBOOK_MIME_TYPES),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    originalSrc: z.string().nullish(),
    src: z.string(),
    contentAttributes: z.object({
        caption: z
            .object({
                richTextFormat: z.array(UnityParagraph),
            })
            .nullish(),
        credit: z.string().nullish(),
    }),
    version: Version,
});
export type UnityFacebook = z.infer<typeof UnityFacebook>;

export const PlateFacebook = z.object({
    type: z.literal(ELEMENT_TYPES.FACEBOOK),
    id: ElementId,
    data: z.object({
        mimetype: z.literal(MIME_TYPES.FACEBOOK_POST),
        templateElement: z.boolean().optional(),
        originalSrc: z.string().nullish(),
        src: z.string(),
        embed: z.object({
            caption: z.array(PlateParagraph),
            credit: z.string(),
        }),
    }),
    children: EmptyChildren,
});
export type FacebookElement = z.infer<typeof PlateFacebook>;

export const FacebookCodec = z.codec(UnityFacebook, PlateFacebook, {
    decode: (value) => ({
        type: value.type,
        id: value.elementId ?? crypto.randomUUID(),
        data: {
            mimetype: MIME_TYPES.FACEBOOK_POST,
            originalSrc: value.originalSrc,
            src: value.src,
            embed: {
                caption: value.contentAttributes.caption
                    ? value.contentAttributes.caption.richTextFormat.map((paragraph) =>
                          ParagraphCodec.decode(paragraph),
                      )
                    : [
                          {
                              type: ELEMENT_TYPES.PARAGRAPH,
                              id: crypto.randomUUID(),
                              children: emptyChildren,
                          },
                      ],
                credit: value.contentAttributes.credit ?? '',
            },
            ...(value.templateElement && { templateElement: value.templateElement }),
        },
        children: emptyChildren,
    }),
    encode: (value) => ({
        type: value.type,
        elementId: value.id,
        version: CURRENT_VERSION,
        ...omit(value.data, ['embed']),
        contentAttributes: {
            caption: {
                richTextFormat: value.data.embed.caption.map((paragraph) =>
                    ParagraphCodec.encode(paragraph),
                ),
            },
            credit: value.data.embed.credit,
        },
    }),
});
